import * as React from "react"

import { value as bound, count as boundCount } from "./bindings"

/* ============================================================
 *  SECTION RENDERERS   (WTM-022)
 * ============================================================
 *  Generated from the Template Maker's own SectionShapes by
 *  scripts/port-shapes — the SHAPES map below is lifted verbatim,
 *  which is the whole point: one description of what a section
 *  looks like, used by the builder's preview and by the exported
 *  site. A design cannot render one way in one and another way in
 *  the other, because there is only one of it.
 *
 *  Do not edit this file. Edit SectionShapes and re-run the port.
 *
 *  The differences are deliberate and few:
 *    - a photo is an image, not a button that opens a picker
 *    - repeating things count down to what the host actually has
 *    - every field reads through the bindings, so the contractor's
 *      own name, services and pictures land in the design's slots
 * ============================================================ */

const pad = (n) => String(n).padStart(2, "0")

const counts = (n) => Array.from({ length: n }, (_, i) => i + 1)

function initialsOf(name) {
  return String(name || "")
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("")
}

/**
 * The shape context. Same surface the builder's renderers expect, so the
 * lifted SHAPES map needs no edits: `v` reads a field, `img` reads a picture,
 * `has` asks whether this section has such a field at all.
 */
function shapeContext(section, view, position, nav) {
  const fields = section.fields || {}
  return {
    definition: { label: section.label || "", fields: [] },
    num: pad(position),
    view,
    has: (key) => Object.prototype.hasOwnProperty.call(fields, key),
    v: (key) => bound(view, key, fields[key], section),
    img: (key) => bound(view, key, fields[key], section),
    // The picker does not exist here. Kept so the lifted map can call it.
    onPickImage: () => {},
    nav,
  }
}

/**
 * The links, and the button that opens them on a phone.
 *
 * Both come from one helper because they are one control: a narrow header
 * sheds the phone number, then the quote button, then puts the links behind a
 * menu icon and drops them down as a panel — which is what the site this was
 * composed from does, and what a visitor expects a header to do. The shedding
 * is in the stylesheet; what has to be here is the pair, as siblings of the
 * bar so the flex row still sees them, which is what the fragment is for.
 *
 * The one difference from the builder: these are real anchors. In the builder
 * a header link is a label, because clicking the header selects the section.
 * On a site it is a link, and it has to go where it says.
 */
function HeaderMenu({ ctx }) {
  const [open, setOpen] = React.useState(false)
  const links = ctx.nav.filter((link) => link.enabled)

  return (
    <>
      <nav className="tm-header-links" data-open={open ? "true" : undefined}>
        {links.map((link) => (
          <a key={link.nodeId} href={link.href} onClick={() => setOpen(false)}>
            {link.label}
          </a>
        ))}
      </nav>
      <button
        type="button"
        className="tm-header-burger"
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        {open ? "✕" : "☰"}
      </button>
    </>
  )
}

function headerMenu(ctx) {
  return <HeaderMenu ctx={ctx} />
}

/**
 * A section composed in the Section Maker.   (WTM-038)
 *
 * The plan travels in a field — rows, the grid each uses, and the bands in
 * each column — so one renderer draws every composed section rather than one
 * per design. It reads a field like any other shape, which is the whole
 * reason it works out here as well as in the builder.
 */
function composedPlan(raw) {
  try {
    const parsed = JSON.parse(raw || "{}")
    const rows = Array.isArray(parsed.rows) ? parsed.rows : []
    return {
      rows: rows.map((row) => ({
        template: typeof row?.template === "string" && row.template ? row.template : "1fr",
        columns: Array.isArray(row?.columns)
          ? row.columns.map((column) => (Array.isArray(column) ? column.map(String) : []))
          : [],
      })),
    }
  } catch {
    // A plan that will not parse draws the heading rather than nothing.
    return { rows: [] }
  }
}

function composedBand(ctx, kind, key) {
  // WTM-046. The three blocks a header is made of.
  if (kind === "brand") {
    return (
      <span key={key} className="tm-header-brand">
        <span className="tm-monogram tm-header-logo" aria-hidden="true">{initialsOf(ctx.v("brandName"))}</span>
        <span>
          <strong>{ctx.v("brandName")}</strong>
          <small>{ctx.v("brandTagline")}</small>
        </span>
      </span>
    )
  }

  if (kind === "nav") {
    return (
      <nav key={key} className="tm-header-links">
        {counts(4).map((n) => <i key={n}>{ctx.v(`link${n}`)}</i>)}
      </nav>
    )
  }

  if (kind === "quote-cta") {
    return (
      <span key={key} className="tm-section-actions">
        <span className="tm-header-phone">
          <small>{ctx.v("phoneLabel")}</small>
          <strong>{ctx.v("phone")}</strong>
        </span>
        <span className="tm-preview-cta">{ctx.v("ctaText")}</span>
      </span>
    )
  }

  if (kind === "highlight") return <span key={key} className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
  if (kind === "title") return <h2 key={key}>{ctx.v("title")}</h2>
  if (kind === "subtitle") return <p key={key}>{ctx.v("subtitle")}</p>

  if (kind === "quick-info") {
    return (
      <div className="tm-point-row" data-count={3} key={key}>
        {counts(3).map((n) => (
          <div className="tm-point" key={n}>
            <strong>{ctx.v(`point${n}`)}</strong>
            <span>{ctx.v(`point${n}Note`)}</span>
          </div>
        ))}
      </div>
    )
  }

  if (kind === "double-button") {
    return (
      <span className="tm-section-actions" key={key}>
        <span className="tm-preview-cta">{ctx.v("button")}</span>
        <span className="tm-preview-cta is-ghost">{ctx.v("buttonSecondary")}</span>
      </span>
    )
  }

  if (kind === "social-proof") {
    return (
      <span className="tm-proof-line" key={key}>
        {stars()}
        <span>{ctx.v("quote")}</span>
      </span>
    )
  }

  if (kind === "form") {
    return (
      <div className="tm-form-card" key={key}>
        <strong>{ctx.v("formTitle")}</strong>
        {["field1", "field2", "field3"].map((field) => (
          <span className="tm-form-input" key={field}>{ctx.v(field)}</span>
        ))}
        <span className="tm-form-submit">{ctx.v("submit")}</span>
        <small>{ctx.v("formNote")}</small>
      </div>
    )
  }

  return null
}

function composed(ctx) {
  const plan = composedPlan(ctx.v("composed"))
  if (plan.rows.length === 0) {
    return (
      <div className="tm-section-content">
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
      </div>
    )
  }

  return (
    <div className="tm-composed">
      {plan.rows.map((row, index) => (
        <div className="tm-composed-row" key={index} style={{ gridTemplateColumns: row.template }}>
          {row.columns.map((bands, position) => (
            <div className="tm-composed-col" key={position}>
              {bands.map((band, at) => composedBand(ctx, band, at))}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Two letters from the business name, standing in for a logo.
 *
 * "Valley Roofing" -> "VR". The host works the same two letters out itself, so
 * use its answer where there is one rather than a second opinion that could
 * disagree with the rest of the site.
 */
function monogram(ctx, options = {}) {
  const view = ctx.view || {}
  const letters = view.initials || initialsOf(view.name)
  if (!letters) return null
  return (
    <span
      key={options.listKey}
      className={["tm-monogram", options.className || ""].filter(Boolean).join(" ")}
      aria-hidden="true"
    >
      {letters}
    </span>
  )
}

/**
 * A picture, or the branded panel when there is none to show.
 *
 * The logo is the exception. Free Contractor Services never asks a contractor
 * for a logo file, so that slot is empty on nearly every site it builds — and
 * an empty grey panel sitting beside the business name reads as a broken
 * image rather than as a design. Where there is no logo the initials of the
 * name stand in, which is what the host's own templates do.
 */
function photo(ctx, key, options = {}) {
  const src = ctx.img(key)
  if (!src && key === "imageLogo") return monogram(ctx, options)
  const classes = ["tm-photo", options.tall ? "is-tall" : "", options.className || ""]
    .filter(Boolean)
    .join(" ")
  return (
    <span
      key={options.listKey}
      className={classes}
      data-empty={src ? undefined : "true"}
      data-slot={key}
      style={src ? { backgroundImage: `url("${src}")` } : undefined}
      role={src ? "img" : undefined}
      aria-label={src ? options.alt || "" : undefined}
    >
      {options.caption ? <i aria-hidden="true">{options.caption}</i> : null}
    </span>
  )
}

function stars(value) {
  return (
    <span className="tm-stars">
      {"\u2605\u2605\u2605\u2605\u2605"}
      {value ? <b>{value}</b> : null}
    </span>
  )
}

function avatar(name) {
  return (
    <span className="tm-avatar" aria-hidden="true">
      {initialsOf(name) || "\u2013"}
    </span>
  )
}

function copy(ctx, hideActions) {
  const showPrimary = !hideActions && ctx.has("button")
  const showSecondary = !hideActions && ctx.has("buttonSecondary")
  return (
    <div className="tm-section-content">
      {ctx.has("eyebrow") ? <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span> : null}
      {ctx.has("title") ? <h2>{ctx.v("title")}</h2> : null}
      {ctx.has("subtitle") ? <p>{ctx.v("subtitle")}</p> : null}
      {showPrimary || showSecondary ? (
        <span className="tm-section-actions">
          {showPrimary ? <span className="tm-preview-cta">{ctx.v("button")}</span> : null}
          {showSecondary ? (
            <span className="tm-preview-cta is-ghost">{ctx.v("buttonSecondary")}</span>
          ) : null}
        </span>
      ) : null}
    </div>
  )
}

function headingSplit(ctx) {
  return (
    <div className="tm-heading-split">
      <div className="tm-section-content">
        {ctx.has("eyebrow") ? <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span> : null}
        <h2>{ctx.v("title")}</h2>
      </div>
      <p className="tm-heading-lead">{ctx.v("subtitle")}</p>
    </div>
  )
}

function statRow(ctx, count, className) {
  return (
    <div className={className} data-count={count}>
      {counts(count).map((i) => (
        <div className="tm-stat" key={i}>
          <strong>{ctx.v(`stat${i}`)}</strong>
          <span>{ctx.v(`stat${i}Label`)}</span>
        </div>
      ))}
    </div>
  )
}

function pointList(ctx, count, className) {
  return (
    <div className={className} data-count={count}>
      {counts(count).map((i) => (
        <div className="tm-point" key={i}>
          <strong>{ctx.v(`point${i}`)}</strong>
          {ctx.has(`point${i}Note`) ? <span>{ctx.v(`point${i}Note`)}</span> : null}
        </div>
      ))}
    </div>
  )
}

function stepList(ctx, count) {
  return (
    <div className="tm-steps" data-count={count}>
      {counts(count).map((i) => (
        <div className="tm-step" key={i}>
          <em>{pad(i)} \u2014</em>
          <strong>{ctx.v(`step${i}`)}</strong>
          <span>{ctx.v(`step${i}Desc`)}</span>
        </div>
      ))}
    </div>
  )
}

/** Tiles come from the gallery, so a short gallery draws a short grid. */
function tiles(ctx, count, numbered) {
  const shown = boundCount(ctx.view, "gallery", count)
  return (
    <div className="tm-tiles" data-count={shown}>
      {counts(shown).map((i) =>
        photo(ctx, `image${i}`, {
          className: "tm-tile",
          caption: numbered ? pad(i) : undefined,
          listKey: i,
        }),
      )}
    </div>
  )
}

function formCard(ctx) {
  const rows = ["field1", "field2", "field3"].filter((key) => ctx.has(key)).map((key) => ctx.v(key))
  return (
    <div className="tm-form-card">
      <strong>{ctx.v("formTitle")}</strong>
      {rows.map((row, i) => (
        <span className="tm-form-input" key={`${row}-${i}`}>
          {row}
        </span>
      ))}
      <span className="tm-form-submit">{ctx.v("submit")}</span>
      {ctx.has("formNote") ? <small>{ctx.v("formNote")}</small> : null}
    </div>
  )
}

function quoteCard(ctx, standalone) {
  return (
    <div className={`tm-review-card ${standalone ? "is-standalone" : ""}`}>
      <em aria-hidden="true">{"\u201c"}</em>
      <p>{ctx.v("quote")}</p>
      <span className="tm-review-by">
        {avatar(ctx.v("author"))}
        <span>
          <strong>{ctx.v("author")}</strong>
          {stars(standalone ? ctx.v("rating") : undefined)}
        </span>
      </span>
    </div>
  )
}

function signature(ctx) {
  return (
    <span className="tm-signature">
      {avatar(ctx.v("author"))}
      <span>
        <strong>{ctx.v("author")}</strong>
        <small>{ctx.v("authorMeta")}</small>
      </span>
    </span>
  )
}

/* -- lifted from SectionShapes ------------------------------------------- */

const SHAPES = {
  /* Hero ---------------------------------------------------- */
  "hero-split": (ctx) => (
    <>
      {copy(ctx)}
      {null}
    </>
  ),

  "hero-form": (ctx) => (
    <>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow">✓ {ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
        <span className="tm-trust-row">
          {counts(3).map((index) => (
            <i key={index}>✓ {ctx.v(`trust${index}`)}</i>
          ))}
        </span>
        <span className="tm-section-actions">
          <span className="tm-preview-cta">{ctx.v("button")}</span>
          <span className="tm-preview-cta is-ghost">{ctx.v("buttonSecondary")}</span>
        </span>
      </div>
      {formCard(ctx)}
      {null}
    </>
  ),

  "hero-photo-review": (ctx) => (
    <>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow">✓ {ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
        <span className="tm-rating-box">
          {stars(ctx.v("rating"))}
          <small>{ctx.v("reviewCount")}</small>
        </span>
        <span className="tm-section-actions">
          <span className="tm-preview-cta">{ctx.v("button")}</span>
        </span>
      </div>
      {quoteCard(ctx)}
      {null}
    </>
  ),

  "hero-centered": (ctx) => copy(ctx),

  "hero-editorial": (ctx) => (
    <>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
        <span className="tm-section-actions">
          <span className="tm-preview-cta">{ctx.v("button")}</span>
          <span className="tm-preview-cta is-ghost">{ctx.v("buttonSecondary")}</span>
        </span>
        <span className="tm-point-row">
          {counts(3).map((index) => (
            <i key={index}>✓ {ctx.v(`point${index}`)}</i>
          ))}
        </span>
      </div>
      <div className="tm-media-stack">
        {photo(ctx, "image", { tall: true })}
        <span className="tm-media-badge">
          <strong>{ctx.v("badgeValue")}</strong>
          <span>{stars()}{ctx.v("badgeLabel")}</span>
        </span>
      </div>
    </>
  ),

  "hero-display": (ctx) => (
    <>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow"><i>{ctx.num}</i> {ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
        <span className="tm-section-actions">
          <span className="tm-preview-cta">{ctx.v("button")} →</span>
          <span className="tm-preview-cta is-line">{ctx.v("buttonSecondary")}</span>
        </span>
        {statRow(ctx, 3, "tm-meta-row")}
      </div>
      <div className="tm-media-stack is-square">
        {photo(ctx, "image", { tall: true })}
        <span className="tm-media-block" aria-hidden="true" />
      </div>
    </>
  ),

  "hero-logo": (ctx) => (
    <>
      <span className="tm-hero-ribbon">{ctx.v("ribbon")} {stars()}</span>
      <div className="tm-section-content is-centered">
        {photo(ctx, "imageLogo", { className: "tm-logo-slot" })}
        <span className="tm-logo-mark">{ctx.v("title")}</span>
        <p>{ctx.v("subtitle")}</p>
        <span className="tm-preview-cta">{ctx.v("button")}</span>
      </div>
      {null}
    </>
  ),

  /* Trust bar ----------------------------------------------- */
  "band-stats": (ctx) => (
    <div className="tm-band-stats">
      {counts(4).map((index) => (
        <div className="tm-band-stat" key={index}>
          <i aria-hidden="true">★</i>
          <span>
            <strong>{ctx.v(`stat${index}`)}</strong>
            <small>{ctx.v(`stat${index}Label`)}</small>
          </span>
        </div>
      ))}
    </div>
  ),

  "band-chips": (ctx) => (
    <div className="tm-band-chips">
      {counts(4).map((index) => (
        <div className="tm-chip" key={index}>
          <i aria-hidden="true">✓</i>
          <span>
            <strong>{ctx.v(`chip${index}`)}</strong>
            <small>{ctx.v(`chip${index}Note`)}</small>
          </span>
        </div>
      ))}
    </div>
  ),

  "band-marquee": (ctx) => (
    <div className="tm-marquee">
      <div className="tm-marquee-track">
        {counts(5).map((index) => (
          <span key={index}>{ctx.v(`item${index}`)} <i aria-hidden="true">★</i></span>
        ))}
      </div>
    </div>
  ),

  "band-claim": (ctx) => <div className="tm-claim-band"><span>{ctx.v("title")}</span></div>,

  /* Stats --------------------------------------------------- */
  "stats-figures": (ctx) => statRow(ctx, 4, "tm-figures"),
  "stats-compact": (ctx) => statRow(ctx, 3, "tm-figures is-compact"),

  /* About us ------------------------------------------------ */
  "about-story": (ctx) => (
    <>
      <div className="tm-media-stack">{photo(ctx, "image", { tall: true })}</div>
      {copy(ctx)}
    </>
  ),

  "about-proof": (ctx) => (
    <>
      {copy(ctx)}
      <div className="tm-proof-points">
        {counts(3).map((index) => (
          <i key={index}>{pad(index)} · {ctx.v(`point${index}`)}</i>
        ))}
      </div>
    </>
  ),

  "about-photo-badge": (ctx) => (
    <>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
        {statRow(ctx, 2, "tm-inline-stats")}
        <span className="tm-highlight-list">
          {counts(3).map((index) => (
            <i key={index}>{ctx.v(`highlight${index}`)}</i>
          ))}
        </span>
        <span className="tm-preview-cta">{ctx.v("button")} →</span>
      </div>
      <div className="tm-media-stack">
        {photo(ctx, "image", { tall: true })}
        <span className="tm-media-strip">✓ {ctx.v("badgeLabel")}</span>
      </div>
    </>
  ),

  "about-why-list": (ctx) => (
    <>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
        {pointList(ctx, 3, "tm-why-list")}
        <span className="tm-preview-cta">{ctx.v("button")} →</span>
      </div>
      <div className="tm-media-stack">
        {photo(ctx, "image", { tall: true })}
        <span className="tm-media-strip">{ctx.v("badgeLabel")}</span>
      </div>
    </>
  ),

  "about-checklist": (ctx) => (
    <>
      <div className="tm-media-stack">
        {photo(ctx, "image", { tall: true })}
        <span className="tm-media-badge is-figure">
          <strong>{ctx.v("badgeValue")}</strong>
          <span>{ctx.v("badgeLabel")}</span>
        </span>
      </div>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
        <span className="tm-check-grid">
          {counts(4).map((index) => (
            <i key={index}>✓ {ctx.v(`check${index}`)}</i>
          ))}
        </span>
        <span className="tm-preview-cta">{ctx.v("button")} →</span>
      </div>
    </>
  ),

  "about-statement": (ctx) => (
    <div className="tm-section-content">
      <span className="tm-section-eyebrow"><i>{ctx.num}</i> {ctx.v("eyebrow")}</span>
      <p className="tm-statement">{ctx.v("title")}</p>
      {signature(ctx)}
    </div>
  ),

  "about-review-badges": (ctx) => (
    <>
      <div className="tm-media-stack">{photo(ctx, "image", { tall: true })}</div>
      <div className="tm-section-content">
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
        <span className="tm-review-badges">
          {["badge1", "badge2"].map((key) => (
            <i key={key}>
              <strong>{ctx.v(key)}</strong>
              {stars()}
            </i>
          ))}
        </span>
      </div>
    </>
  ),

  "about-trust-grid": (ctx) => (
    <>
      <div className="tm-section-content is-centered">
        <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
      </div>
      {pointList(ctx, 3, "tm-trust-grid")}
    </>
  ),

  /* Services ------------------------------------------------ */
  "services-grid": (ctx) => (
    <>
      {copy(ctx)}
      <div className="tm-cards">
        {counts(3).map((index) => (
          <div className="tm-card" key={index}>
            {photo(ctx, `image${index}`)}
            <strong>{ctx.v(`card${index}Title`)}</strong>
          </div>
        ))}
      </div>
    </>
  ),

  "services-list": (ctx) => (
    <>
      {copy(ctx)}
      <div className="tm-rows is-plain">
        {counts(3).map((index) => (
          <div className="tm-row" key={index}>
            <strong>{ctx.v(`row${index}Title`)}</strong>
            <i aria-hidden="true">→</i>
          </div>
        ))}
      </div>
    </>
  ),

  "services-cards-rich": (ctx) => (
    <>
      {copy(ctx, true)}
      <div className="tm-cards is-rich">
        {counts(3).map((index) => (
          <div className="tm-card" key={index}>
            {photo(ctx, `image${index}`)}
            <strong>{ctx.v(`card${index}Title`)}</strong>
            <small>{ctx.v(`card${index}Desc`)}</small>
            <span className="tm-card-features">
              <i>✓ {ctx.v("feature1")}</i>
              <i>✓ {ctx.v("feature2")}</i>
            </span>
            <span className="tm-card-action">{ctx.v("button")} →</span>
          </div>
        ))}
      </div>
    </>
  ),

  "services-cards-photo": (ctx) => (
    <>
      <div className="tm-section-content is-centered">
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
      </div>
      <div className="tm-cards is-photo">
        {counts(3).map((index) => (
          <div className="tm-card" key={index}>
            {photo(ctx, `image${index}`)}
            <strong>{ctx.v(`card${index}Title`)}</strong>
            <small>{ctx.v(`card${index}Desc`)}</small>
          </div>
        ))}
      </div>
    </>
  ),

  "services-rows": (ctx) => (
    <>
      {copy(ctx)}
      <div className="tm-rows">
        {counts(3).map((index) => (
          <div className="tm-row is-numbered" key={index}>
            <em>{pad(index)}</em>
            <span>
              <strong>{ctx.v(`row${index}Title`)}</strong>
              <small>{ctx.v(`row${index}Desc`)}</small>
            </span>
            {photo(ctx, `image${index}`, { className: "tm-row-thumb" })}
            <i aria-hidden="true">→</i>
          </div>
        ))}
      </div>
    </>
  ),

  "services-blocks": (ctx) => (
    <>
      {copy(ctx)}
      <div className="tm-blocks">
        {counts(2).map((index) => (
          <div className="tm-block" key={index}>
            {photo(ctx, `image${index}`, { className: "tm-block-media" })}
            <span>
              <em>{pad(index)} —</em>
              <strong>{ctx.v(`block${index}Title`)}</strong>
              <small>{ctx.v(`block${index}Desc`)}</small>
              <span className="tm-block-points">
                <i>✓ {ctx.v("point1")}</i>
                <i>✓ {ctx.v("point2")}</i>
              </span>
            </span>
          </div>
        ))}
      </div>
    </>
  ),

  "services-helper": (ctx) => (
    <div className="tm-helper-bar">
      <span>
        <strong>{ctx.v("title")}</strong>
        <small>{ctx.v("subtitle")}</small>
      </span>
      <span className="tm-section-actions">
        <span className="tm-preview-cta">{ctx.v("button")}</span>
        <span className="tm-preview-cta is-ghost">{ctx.v("buttonSecondary")}</span>
      </span>
    </div>
  ),

  /* Process ------------------------------------------------- */
  "process-steps": (ctx) => (
    <>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow"><i>{ctx.num}</i> {ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
      </div>
      {stepList(ctx, 4)}
    </>
  ),

  "process-three": (ctx) => (
    <>
      {copy(ctx)}
      {stepList(ctx, 3)}
    </>
  ),

  /* Our work ------------------------------------------------ */
  gallery: (ctx) => (
    <>
      {copy(ctx)}
      {tiles(ctx, 3)}
    </>
  ),

  "gallery-six": (ctx) => (
    <>
      {copy(ctx)}
      {tiles(ctx, 6)}
    </>
  ),

  "gallery-intro": (ctx) => (
    <>
      <div className="tm-section-content is-narrow">
        <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
        <p>{ctx.v("subtitle")}</p>
      </div>
      {tiles(ctx, 4)}
    </>
  ),

  "gallery-numbered": (ctx) => (
    <>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
      </div>
      {tiles(ctx, 6, true)}
    </>
  ),

  "gallery-more": (ctx) => (
    <>
      <div className="tm-section-content is-centered">
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
      </div>
      {tiles(ctx, 6)}
      <span className="tm-pill-button">{ctx.v("button")}</span>
    </>
  ),

  /* Testimonials -------------------------------------------- */
  "testimonial-centered": (ctx) => (
    <div className="tm-quote-block">
      <em aria-hidden="true">“</em>
      <p>{ctx.v("quote")}</p>
      {stars(ctx.v("rating"))}
      {signature(ctx)}
    </div>
  ),

  "testimonial-split": (ctx) => (
    <div className="tm-quote-split">
      <div>
        <span className="tm-section-eyebrow"><i>{ctx.num}</i> {ctx.v("eyebrow")}</span>
        <span className="tm-score">
          <strong>{ctx.v("rating")}</strong>
          {stars()}
          <small>{ctx.v("reviewCount")}</small>
        </span>
      </div>
      <div>
        <p>{ctx.v("quote")}</p>
        {signature(ctx)}
      </div>
    </div>
  ),

  "testimonial-card": (ctx) => (
    <>
      <div className="tm-section-content is-centered">
        <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
      </div>
      {quoteCard(ctx, true)}
    </>
  ),

  /* Call to action ------------------------------------------ */
  cta: (ctx) => copy(ctx),

  "cta-trust": (ctx) => (
    <>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
        <span className="tm-section-actions">
          <span className="tm-preview-cta">{ctx.v("button")}</span>
          <span className="tm-preview-cta is-ghost">{ctx.v("buttonSecondary")}</span>
        </span>
      </div>
      {pointList(ctx, 3, "tm-cta-trust")}
      {null}
    </>
  ),

  "cta-split": (ctx) => (
    <div className="tm-cta-split">
      <span>
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
      </span>
      <span className="tm-section-actions">
        <span className="tm-preview-cta">{ctx.v("button")}</span>
        <span className="tm-preview-cta is-ghost">{ctx.v("buttonSecondary")}</span>
      </span>
    </div>
  ),

  /* Contact ------------------------------------------------- */
  "contact-defs": (ctx) => (
    <>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow"><i>{ctx.num}</i> {ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
        <span className="tm-contact-phone">{ctx.v("phone")}</span>
        <p>{ctx.v("subtitle")}</p>
      </div>
      <dl className="tm-def-list">
        {counts(3).map((index) => (
          <div key={index}>
            <dt>{ctx.v(`term${index}`)}</dt>
            <dd>{ctx.v(`value${index}`)}</dd>
          </div>
        ))}
      </dl>
    </>
  ),

  "contact-photo": (ctx) => (
    <>
      <div className="tm-section-content is-centered">
        <h2>{ctx.v("title")}</h2>
        <span className="tm-rule" aria-hidden="true" />
        <p>{ctx.v("subtitle")}</p>
        <span className="tm-pill-button">{ctx.v("button")}</span>
        <span className="tm-social-row" aria-hidden="true"><i>f</i><i>@</i><i>☏</i></span>
      </div>
      {null}
    </>
  ),

  "contact-form": (ctx) => (
    <>
      {copy(ctx)}
      {formCard(ctx)}
    </>
  ),

  /* Footer -------------------------------------------------- */
  footer: (ctx) => copy(ctx),

  "footer-columns": (ctx) => (
    <div className="tm-footer-grid">
      <div>
        <strong>{ctx.v("title")}</strong>
        <small>{ctx.v("subtitle")}</small>
        <span className="tm-social-row" aria-hidden="true"><i>f</i><i>in</i><i>@</i></span>
      </div>
      {["col1Title", "col2Title", "col3Title"].map((key) => (
        <div className="tm-footer-col" key={key}>
          <strong>{ctx.v(key)}</strong>
          <i aria-hidden="true" /><i aria-hidden="true" /><i aria-hidden="true" />
        </div>
      ))}
      <span className="tm-footer-bottom">{ctx.v("copyright")}</span>
    </div>
  ),

  "footer-cta": (ctx) => (
    <div className="tm-footer-cta">
      <div>
        <h2>{ctx.v("title")}</h2>
        <span className="tm-preview-cta">{ctx.v("button")} →</span>
      </div>
      <span className="tm-footer-bottom">
        <small>{ctx.v("subtitle")}</small>
        <small>{ctx.v("copyright")}</small>
      </span>
    </div>
  ),

  "footer-brand": (ctx) => (
    <div className="tm-footer-brand">
      <div>
        <strong>{ctx.v("title")}</strong>
        <small>{ctx.v("subtitle")}</small>
      </div>
      <span className="tm-brand-badge" aria-hidden="true">{initialsOf(ctx.v("title"))}</span>
      <span className="tm-footer-bottom">{ctx.v("copyright")}</span>
    </div>
  ),

  /* ── Header (WTM-021) ──────────────────────────────────────── */

  "header-bar": (ctx) => (
    <div className="tm-header-bar">
      <div className="tm-header-topbar">
        <span>◎ {ctx.v("topbarLeft")}</span>
        <span>◷ {ctx.v("topbarRight")} <i aria-hidden="true">f</i></span>
      </div>
      <div className="tm-header-nav">
        <span className="tm-header-brand">
          {photo(ctx, "imageLogo", { className: "tm-header-logo" })}
          <span>
            <strong>{ctx.v("brandName")}</strong>
            <small>{ctx.v("brandTagline")}</small>
          </span>
        </span>
        {headerMenu(ctx)}
        <span className="tm-header-phone">
          <small>{ctx.v("phoneLabel")}</small>
          <strong>{ctx.v("phone")}</strong>
        </span>
        <span className="tm-preview-cta">{ctx.v("button")} ↗</span>
      </div>
    </div>
  ),

  /* ── Factory Direct Flooring (WTM-016) ─────────────────────── */

  "composed-section": (ctx) => composed(ctx),

  "hero-benefits-form": (ctx) => (
    <>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
        <span className="tm-benefit-list">
          {counts(3).map((index) => (
            <i key={index}>
              <b>{ctx.v(`benefit${index}`)}</b>
              <small>{ctx.v(`benefit${index}Note`)}</small>
            </i>
          ))}
        </span>
        <span className="tm-section-actions">
          <span className="tm-preview-cta">{ctx.v("button")}</span>
          <span className="tm-preview-cta is-ghost">{ctx.v("buttonSecondary")}</span>
        </span>
        <span className="tm-proof-line">{stars()}{ctx.v("proof")}</span>
      </div>
      <div className="tm-form-card is-tall">
        <span className="tm-form-kicker">{ctx.v("formKicker")}</span>
        <strong>{ctx.v("formTitle")}</strong>
        <small>{ctx.v("formNote")}</small>
        {counts(4).map((index) => (
          <span className="tm-form-input" key={index}>{ctx.v(`field${index}`)}</span>
        ))}
        <span className="tm-form-submit">{ctx.v("submit")}</span>
      </div>
      {null}
    </>
  ),

  "band-utility": (ctx) => (
    <div className="tm-utility-bar">
      <span>{ctx.v("item1")}</span>
      <span>{ctx.v("item2")}</span>
      <span>{ctx.v("item3")}</span>
    </div>
  ),

  "band-icon-notes": (ctx) => (
    <div className="tm-icon-notes">
      {counts(4).map((index) => (
        <div key={index}>
          <i aria-hidden="true">✦</i>
          <span>
            <strong>{ctx.v(`chip${index}`)}</strong>
            <small>{ctx.v(`chip${index}Note`)}</small>
          </span>
        </div>
      ))}
    </div>
  ),

  "band-statements": (ctx) => (
    <div className="tm-statement-strip">
      <span>{ctx.v("item1")}</span>
      <span>{ctx.v("item2")} <i aria-hidden="true">→</i></span>
    </div>
  ),

  "services-numbered-cards": (ctx) => (
    <>
      {headingSplit(ctx)}
      <div className="tm-split-cards">
        {counts(4).map((index) => (
          <article className="tm-split-card" key={index}>
            <span className="tm-split-card-media">
              {photo(ctx, `image${index}`)}
              <i aria-hidden="true">{pad(index)}</i>
            </span>
            <span className="tm-split-card-body">
              <i className="tm-split-card-icon" aria-hidden="true">◆</i>
              <small className="tm-card-kicker">{ctx.v(`card${index}Kicker`)}</small>
              <strong>{ctx.v(`card${index}Title`)}</strong>
              <small>{ctx.v(`card${index}Desc`)}</small>
              <span className="tm-text-link">{ctx.v("button")} {ctx.v(`card${index}Title`)} →</span>
            </span>
          </article>
        ))}
      </div>
      <div className="tm-service-foot">
        <span>{ctx.v("footerText")}</span>
        <span className="tm-text-link">{ctx.v("footerLink")} ↗</span>
      </div>
    </>
  ),

  "about-promise": (ctx) => (
    <div className="tm-promise">
      <span className="tm-promise-mark" aria-hidden="true">◆</span>
      <h2>{ctx.v("title")}</h2>
      <p>{ctx.v("subtitle")}</p>
      <span className="tm-circle-link" aria-hidden="true">↗</span>
    </div>
  ),

  "about-local-badge": (ctx) => (
    <>
      <div className="tm-media-stack">
        {photo(ctx, "image", { tall: true })}
        <span className="tm-local-badge">
          <strong>{ctx.v("badgeTitle")}</strong>
          <small>{ctx.v("badgeLabel")}</small>
        </span>
      </div>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
        <p>{ctx.v("body")}</p>
        <span className="tm-check-grid">
          {counts(4).map((index) => (
            <i key={index}>✓ {ctx.v(`check${index}`)}</i>
          ))}
        </span>
        <span className="tm-preview-cta">{ctx.v("button")} ↗</span>
      </div>
    </>
  ),

  "gallery-filtered": (ctx) => (
    <>
      {copy(ctx)}
      <span className="tm-filter-row">
        {counts(5).map((index) => (
          <i key={index} data-active={index === 1 ? "true" : undefined}>{ctx.v(`filter${index}`)}</i>
        ))}
      </span>
      <div className="tm-work-grid">
        {counts(6).map((index) => (
          <span className="tm-work-tile" key={index}>
            {photo(ctx, `image${index}`)}
            <span className="tm-work-label">
              <span>
                <small>{ctx.v(`tile${index}Type`)}</small>
                <strong>{ctx.v(`tile${index}Title`)}</strong>
              </span>
              <i aria-hidden="true">+</i>
            </span>
          </span>
        ))}
      </div>
      <small className="tm-gallery-note">{ctx.v("note")}</small>
    </>
  ),

  "testimonial-carousel": (ctx) => (
    <>
      <div className="tm-carousel-head">
        <div className="tm-section-content">
          <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
          <h2>{ctx.v("title")}</h2>
        </div>
        <span className="tm-slider-controls" aria-hidden="true"><i>←</i><i>→</i></span>
      </div>
      <div className="tm-carousel">
        {counts(3).map((index) => (
          <div className="tm-review-card" key={index}>
            <span className="tm-review-top">{stars()}<em aria-hidden="true">“</em></span>
            <p>{ctx.v(`quote${index}`)}</p>
            <span className="tm-review-by">
              {avatar(ctx.v(`author${index}`))}
              <span>
                <strong>{ctx.v(`author${index}`)}</strong>
                <small>{ctx.v(`meta${index}`)}</small>
              </span>
            </span>
          </div>
        ))}
      </div>
      <small className="tm-gallery-note">{ctx.v("note")}</small>
    </>
  ),

  "contact-showroom-faq": (ctx) => (
    <>
      <div className="tm-section-content">
        <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
        <span className="tm-detail-list">
          <i>{ctx.v("address")}</i>
          <i>{ctx.v("hours")}</i>
          <i>{ctx.v("phone")}</i>
        </span>
        <span className="tm-city-tags">
          <i>{ctx.v("tag1")}</i>
          <i>{ctx.v("tag2")}</i>
          <i>{ctx.v("tag3")}</i>
        </span>
      </div>
      <div className="tm-faq">
        <span className="tm-section-eyebrow">{ctx.v("faqEyebrow")}</span>
        {counts(4).map((index) => (
          <div className="tm-faq-row" key={index}>
            <strong>{ctx.v(`faq${index}Question`)}<i aria-hidden="true">+</i></strong>
            {index === 1 && <small>{ctx.v(`faq${index}Answer`)}</small>}
          </div>
        ))}
      </div>
    </>
  ),

  "cta-photo-phone": (ctx) => (
    <>
      <div className="tm-section-content is-centered">
        <span className="tm-section-eyebrow">{ctx.v("eyebrow")}</span>
        <h2>{ctx.v("title")}</h2>
        <p>{ctx.v("subtitle")}</p>
        <span className="tm-section-actions">
          <span className="tm-preview-cta">{ctx.v("button")} ↗</span>
          <span className="tm-preview-cta is-ghost">{ctx.v("buttonSecondary")}</span>
        </span>
      </div>
      {null}
    </>
  ),

  "footer-explore": (ctx) => (
    <div className="tm-footer-grid">
      <div>
        <strong>{ctx.v("title")}</strong>
        <small>{ctx.v("subtitle")}</small>
        <span className="tm-social-row" aria-hidden="true"><i>f</i></span>
      </div>
      <div className="tm-footer-col">
        <strong>{ctx.v("col1Title")}</strong>
        <i aria-hidden="true" /><i aria-hidden="true" /><i aria-hidden="true" />
      </div>
      <div>
        <strong>{ctx.v("col2Title")}</strong>
        <small>{ctx.v("col2Body")}</small>
      </div>
      <div>
        <strong>{ctx.v("col3Title")}</strong>
        <small>{ctx.v("col3Body")}</small>
        <span className="tm-footer-phone">{ctx.v("phone")} ↗</span>
        <small>{ctx.v("note")}</small>
      </div>
      <span className="tm-footer-bottom">{ctx.v("copyright")}</span>
    </div>
  ),}

/** The variants whose picture is the section's own background. */
const BACKDROP = {
  "hero-split": "side",
  "hero-form": "side",
  "hero-photo-review": "side",
  "cta-trust": "side",
  "hero-logo": "centred",
  "contact-photo": "centred",
  "hero-benefits-form": "side",
  "cta-photo-phone": "centred",
}

export function backdropStyle(section, view) {
  const kind = BACKDROP[section.variant]
  if (!kind) return undefined
  const src = bound(view, "image", (section.fields || {}).image)
  if (!src) return undefined
  const overlay = kind === "centred" ? "var(--hero-overlay-centred)" : "var(--hero-overlay)"
  return { backgroundImage: `${overlay}, url("${src}")` }
}

/* -- navigation ----------------------------------------------------------- */

/**
 * Which pools a header links to, and what the link is called.
 *
 * The same table the builder derives its nav from, so the preview's links and
 * the exported site's links are the same links.
 */
const LINKABLE = {
  "About us": "About",
  Services: "Services",
  Process: "How it works",
  "Our work": "Past projects",
  Testimonials: "Reviews",
  "Call to action": "Request quote",
  Contact: "Contact",
}

/** The anchor a section answers to. Stable, and derived from its pool. */
export function anchorFor(category) {
  return String(category || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
}

/**
 * The nav, built from the sections actually on the page.
 *
 * A link can never point at a section that is not there, because the list is
 * made from the sections that are. Overrides the author set in Config travel
 * on the header section under `nav:` keys and are applied here, so a renamed
 * or switched-off link stays renamed or switched off on the site.
 */
export function navigationLinks(sections) {
  const header = sections.find((section) => section.category === "Header")
  const overrides = (header && header.fields) || {}
  const seen = new Set()
  const links = []

  sections.forEach((section, index) => {
    const label = LINKABLE[section.category]
    if (!label || seen.has(section.category)) return
    seen.add(section.category)
    const id = `${anchorFor(section.category)}-${index}`
    const nodeId = section.nodeId || id
    if (overrides[`nav:${nodeId}`] === "off") return
    links.push({
      nodeId,
      href: `#${anchorFor(section.category)}`,
      label: (overrides[`nav:${nodeId}:label`] || "").trim() || label,
      enabled: true,
      overridden: Boolean((overrides[`nav:${nodeId}:label`] || "").trim()),
      index,
      targetTitle: section.fields && section.fields.title ? section.fields.title : label,
    })
  })

  return links
}

/** One section, rendered. Unknown variants draw nothing rather than throwing. */
export function renderSection(section, view, position, nav) {
  const render = SHAPES[section.variant]
  if (!render) return null
  return render(shapeContext(section, view, position, nav || []))
}

export const KNOWN_VARIANTS = Object.keys(SHAPES)
